import React, { memo, useCallback, useRef, useState } from 'react';
import { Handle, Position, NodeProps, Node } from '@xyflow/react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  InfoIcon, 
  ChevronDownIcon, 
  ChevronUpIcon, 
  AlertCircleIcon,
  PlusIcon,
  PlayIcon,
  MoreHorizontalIcon,
  EyeIcon,
  FileTextIcon,
  RefreshCwIcon,
  Settings2Icon
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TopicNode as TopicNodeType, NodeData as TopicNodeData } from '@/types/nodeTypes';
import { getStatusVariant, getStatusLabel, Status } from '@/types/nodeTypes';
import { getPhaseColor, getPhaseName } from '@/types/phaseTypes';
import { cn, getImportanceClass } from '@/lib/utils';
import { getNodeBorderColor, getNodeShadow, getPhaseTabColor } from '@/lib/colors';
import { useMapState, useMapExtraction, useMapUI } from '@/contexts/map';

/**
 * ReactFlowノードデータ型定義 - @xyflow/reactの型要件に合わせて拡張
 */
interface ReactFlowNodeData extends TopicNodeData, Record<string, unknown> {}

/**
 * ノードの追加プロップ
 */
interface TopicNodeProps extends NodeProps<Node<ReactFlowNodeData>> {
  onOpenDetails?: (nodeId: string) => void;
}

/**
 * PhaseChip component
 * トピックのフェーズを表すチップコンポーネント
 */
const PhaseChip = ({ phaseId, active = true }: { phaseId: string, active?: boolean }) => {
  const { mapData } = useMapState();
  const phases = mapData?.phases || [];
  
  const phaseColor = getPhaseColor(phaseId, phases);
  const phaseName = getPhaseName(phaseId, phases);
  
  return (
    <div 
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium text-white w-fit transition-colors ${!active ? 'bg-gray-400' : ''}`}
      style={{ backgroundColor: active ? getPhaseTabColor(phaseColor) : undefined }}
    >
      {phaseName}
    </div>
  );
};

/**
 * ImportanceIndicator component
 */
const ImportanceIndicator = ({ level, active = true }: { level: number, active?: boolean }) => {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "w-1.5 h-1.5 rounded-full",
            i < level 
              ? (active ? "bg-foreground" : "bg-gray-400") 
              : (active ? "bg-foreground/20" : "bg-gray-200")
          )}
        />
      ))}
    </div>
  );
};

/**
 * ContentItem component for displaying individual content
 */
const ContentItem = ({ content, active = true }: { content: string, timestamp?: string, active?: boolean }) => {
  return (
    <div className={`${!active ? 'text-gray-500' : ''}`}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`text-xs truncate font-medium ${!active ? 'text-gray-500' : ''}`}>
            {content}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs max-w-[300px] break-words">{content}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

/**
 * ContentItemList component for popover display
 */
const ContentItemList = ({ contents, active = true }: { contents: any[], active?: boolean }) => {
  return (
    <ScrollArea className="h-60 w-full">
      <div className="p-1">
        <div className="border rounded divide-y">
          {contents.map((item, index) => (
            <div key={index} className="px-2 py-1.5">
              <ContentItem 
                content={item.content} 
                active={active}
              />
            </div>
          ))}
        </div>
      </div>
    </ScrollArea>
  );
};

/**
 * Get status color based on status
 */
function getStatusColor(status: Status): string {
  switch (status) {
    case 'success':
      return '#10b981'; // green
    case 'error':
      return '#ef4444'; // red
    case 'partial':
      return '#f59e0b'; // amber
    case 'processing':
      return '#6366f1'; // indigo
    default:
      return '#94a3b8'; // slate
  }
}

/**
 * Status dot component
 */
const StatusDot = ({ 
  status, 
  error, 
  active = true,
  className = ''
}: { 
  status: Status, 
  error: any, 
  active?: boolean,
  className?: string
}) => {
  const statusColor = active ? getStatusColor(status) : '#94a3b8'; // Use slate for inactive nodes
  const statusLabel = getStatusLabel(status);
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={cn(
              "w-2.5 h-2.5 rounded-full cursor-help",
              status === 'processing' && "animate-pulse"
            )}
            style={{ backgroundColor: statusColor }} 
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="flex flex-col gap-1 max-w-[200px]">
          <div className="font-medium">{statusLabel}</div>
          {status === 'processing' && (
            <div className="text-xs text-blue-500">
              処理中...データ抽出を実行しています
            </div>
          )}
          {error && (
            <div className="text-xs flex items-start gap-1 text-destructive">
              <AlertCircleIcon className="w-3 h-3 mt-0.5 shrink-0" />
              <span>{error.message}</span>
            </div>
          )}
          {!active && <div className="text-xs text-gray-500">このトピックは非アクティブです</div>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * TopicNode component
 */
const TopicNode = ({ data, selected, id }: NodeProps<Node<ReactFlowNodeData>>) => {
  // MapUIコンテキストから選択関数を取得
  const { selectNode } = useMapUI();
  const nodeData = data;
  const statusLabel = getStatusLabel(nodeData.status);
  const importanceClass = getImportanceClass(nodeData.importance);
  const isActive = nodeData.active;

  // 抽出処理のためのフック
  const { mapData, updateNode } = useMapState();
  const { extractNodeContent } = useMapExtraction();
  const { transcript } = useMapUI();
  const isProcessing = nodeData.status === 'processing';

  // 個別抽出ボタンのクリックハンドラー
  const handleExtract = async (e: React.MouseEvent) => {
    e.stopPropagation(); // ノード選択イベントを防止
    
    if (!mapData || !transcript || isProcessing) return;
    
    // 対応するTopicNodeを取得
    const node = mapData.nodes.find(n => n.id === nodeData.id);
    if (!node) return;
    
    // 抽出処理
    try {
      const updatedNode = await extractNodeContent(node, transcript);
      if (updatedNode) {
        updateNode(updatedNode.id, updatedNode);
      }
    } catch (error) {
      console.error('Node extraction error:', error);
    }
  };
  
  // State for expanded view
  const [expanded, setExpanded] = useState(false);
  // Reference for measuring content height
  const contentRef = useRef<HTMLDivElement>(null);

  // 固定の高さを設定
  const [nodeHeight, setNodeHeight] = useState<number>(180); // 高さを290pxに増加
  
  // 高さの自動調整は行わない（固定高さを使用）
  
  // Toggle expanded state
  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(prev => !prev);
  };
  
  // Number of visible content items
  const VISIBLE_ITEMS = 2;
  
  // Number of content items
  const contentCount = nodeData.contents?.length || 0;
  
  // Number of additional items
  const additionalItems = Math.max(0, contentCount - VISIBLE_ITEMS);
  
  return (
    <div className={cn(
      "transition-all duration-300", 
      importanceClass,
      isProcessing && "relative" // 処理中の場合は相対位置設定
    )}>
      {/* Add handles on both left and right sides */}
      <Handle type="target" position={Position.Left} className="w-3 h-3" />
      <Handle type="source" position={Position.Right} className="w-3 h-3" />
      
      {/* 処理中の場合にパルスエフェクトを追加 */}
      {isProcessing && (
        <div className="absolute -inset-1 bg-indigo-500/10 rounded-lg blur-sm animate-pulse -z-10" />
      )}
      
      <Card
        className={cn(
          "border transition-all duration-300 py-3 gap-2",
          getNodeShadow(selected),
          selected && "scale-105 z-10",
          !isActive && "border-dashed", // Dashed border for inactive nodes
          isActive ? "bg-white" : "bg-gray-50", // Gray background for inactive nodes
          isProcessing && "ring-2 ring-indigo-500/50 ring-opacity-50 animate-pulse" // 処理中は点滅する枠線
        )}
        style={{ 
          width: `${nodeData.width || 250}px`,
          height: `${nodeHeight}px`,
          borderColor: isActive ? getNodeBorderColor(selected) : '#d1d5db', // Gray border for inactive nodes
          transform: selected ? 'scale(1.05)' : 'scale(1)'
        }}
      >
        <CardHeader className="pb-1 px-3">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <PhaseChip phaseId={nodeData.phaseId} active={isActive} />
                <div className={cn(
                  "w-2.5 h-2.5 rounded-full",
                  nodeData.status === 'processing' && "animate-pulse"
                )}
                style={{ backgroundColor: getStatusColor(nodeData.status) }} 
                />
                {nodeData.status === 'processing' && (
                  <span className="text-xs text-blue-500 animate-pulse">処理中...</span>
                )}
              </div>
              
              {/* エリプシスメニューでアクションをまとめる */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation(); // ノード選択イベントの伝播を防止
                    }}
                  >
                    <MoreHorizontalIcon className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-48"
                  onClick={(e) => {
                    e.stopPropagation(); // ノード選択イベントの伝播を防止
                  }}
                >
                  <DropdownMenuLabel className="text-xs">アクション</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation(); // ノード選択イベントの伝播を防止
                      handleExtract(e);
                    }}
                    disabled={isProcessing}
                    className="text-xs cursor-pointer"
                  >
                    {isProcessing ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-foreground mr-2" />
                    ) : (
                      <PlayIcon className="h-3.5 w-3.5 mr-2" />
                    )}
                    トピックを抽出
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem 
                    className="text-xs cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation(); // ノード選択イベントの伝播を防止
                      // 詳細ドロワーを開く
                      selectNode(nodeData.id as string);
                    }}
                  >
                    <EyeIcon className="h-3.5 w-3.5 mr-2" />
                    詳細を表示
                  </DropdownMenuItem>                
                  
                  {nodeData.status === 'error' && (
                    <DropdownMenuItem 
                      className="text-xs cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation(); // ノード選択イベントの伝播を防止
                      }}
                    >
                      <RefreshCwIcon className="h-3.5 w-3.5 mr-2" />
                      再試行
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem 
                    className="text-xs cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation(); // ノード選択イベントの伝播を防止
                    }}
                  >
                    <Settings2Icon className="h-3.5 w-3.5 mr-2" />
                    ノード設定
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="flex items-center justify-between">
              <h3 className={`text-sm font-medium ${!isActive ? 'text-gray-500' : ''}`}>{nodeData.title}</h3>
            </div>
          </div>
        </CardHeader>
        
        <CardContent ref={contentRef} className={`text-xs px-3 pb-3 ${!isActive ? 'text-gray-500' : ''}`}>
          {nodeData.status === 'processing' ? (
            <div className="flex items-center justify-center py-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-foreground" />
              <span className="ml-2 text-muted-foreground">処理中...</span>
            </div>
          ) : !isActive ? (
            <p className="text-gray-500 italic">言及なし</p>
          ) : contentCount > 0 ? (
            <div className="border rounded divide-y overflow-hidden" style={{ maxHeight: "180px" }}>
              {/* 表示アイテム数を制限 */}
              {nodeData.contents.slice(0, VISIBLE_ITEMS).map((item, index) => (
                <div key={index} className="px-2 py-1.5">
                  <ContentItem 
                    content={item.content} 
                    active={isActive}
                  />
                </div>
              ))}
              
              {/* 追加アイテムがある場合は +N ボタンを表示（カード内） */}
              {additionalItems > 0 && (
                <div className="bg-muted/30 py-0.5 px-1">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className={`w-full h-5 text-xs ${!isActive ? 'text-gray-500 hover:text-gray-600' : ''}`}
                      >
                        <span className="flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                            <path d="M12 5v14M5 12h14"/>
                          </svg>
                          他 {additionalItems} 件
                        </span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-3" align="center" side="bottom">
                      <div className="text-sm font-medium mb-2">すべての言及</div>
                      <ContentItemList 
                        contents={nodeData.contents}
                        active={isActive}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>
          ) : (
            <p className={`line-clamp-3 ${!isActive ? 'text-gray-500' : ''}`}>{nodeData.content || "内容なし"}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default memo(TopicNode);